'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import styles from './contact.module.css';

// ─────────────────────────────────────────────────────────────
// CONFIGURATION
const RECAPTCHA_SITE_KEY = '6LdJBqAsAAAAACd-qmWCjf75F7Xxi3Ucz_6Q35_5';
// ─────────────────────────────────────────────────────────────

declare global {
  interface Window { grecaptcha: any; onRecaptchaLoad: any; }
}

type Status = 'idle' | 'success' | 'error';

function ContactForm() {
  const searchParams = useSearchParams();
  const [name,    setName]    = useState('');
  const [email,   setEmail]   = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [errors,  setErrors]  = useState<Record<string, string>>({});
  const [status,  setStatus]  = useState<Status>(
    searchParams.get('success') === 'true' ? 'success' : 'idle'
  );

  /* Load reCAPTCHA v2 script once */
  useEffect(() => {
    if (document.getElementById('recaptcha-script')) return;
    const script = document.createElement('script');
    script.id    = 'recaptcha-script';
    script.src   = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    // Auto-reset reCAPTCHA when token expires (2 min Google limit)
    (window as any).onRecaptchaLoad = () => {
      window.grecaptcha.render('recaptcha-container', {
        sitekey: RECAPTCHA_SITE_KEY,
        expired_callback: () => window.grecaptcha.reset(),
      });
    };
  }, []);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim())    e.name    = 'Name is required';
    if (!email.trim())   e.email   = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email address';
    if (!subject.trim()) e.subject = 'Subject is required';
    if (!message.trim()) e.message = 'Message is required';
    else if (message.trim().length < 10) e.message = 'Message must be at least 10 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const token = window.grecaptcha?.getResponse();
    if (!token) {
      setErrors(prev => ({ ...prev, recaptcha: 'Please complete the reCAPTCHA' }));
      return;
    }
    (e.target as HTMLFormElement).submit();
  };

  const clearError = (field: string) =>
    setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  return (
    <div className={styles.layout}>

      {/* Left — Info Panel */}
      <div className={styles.infoPanel}>
        <h1 className={styles.infoTitle}>Get in Touch</h1>
        <p className={styles.infoText}>
          Have a question about our AI voice agents? Want to see a demo?
          Fill out the form and we'll get back to you within one business day.
        </p>

        <div className={styles.infoItems}>
          <div className={styles.infoItem}>
            <span className={styles.infoIcon}>✉️</span>
            <div>
              <p className={styles.infoLabel}>Email</p>
              <a href={`mailto:${process.env.NEXT_PUBLIC_CONTACT_EMAIL}`} className={styles.infoValue}>{process.env.NEXT_PUBLIC_CONTACT_EMAIL}</a>
            </div>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoIcon}>⏱️</span>
            <div>
              <p className={styles.infoLabel}>Response Time</p>
              <p className={styles.infoValue}>Within 1 business day</p>
            </div>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoIcon}>🏥</span>
            <div>
              <p className={styles.infoLabel}>Healthcare Inquiries</p>
              <p className={styles.infoValue}>HIPAA-aligned platform — ask us anything</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right — Form Card */}
      <div className={styles.card}>

        {status === 'success' ? (
          <div className={styles.successState}>
            <div className={styles.successIcon}>✅</div>
            <h2 className={styles.successTitle}>Message Sent!</h2>
            <p className={styles.successText}>
              Thanks for reaching out. We'll get back to you within one business day.
            </p>
            <button className={styles.btnReset} onClick={() => setStatus('idle')}>
              Send Another Message
            </button>
          </div>
        ) : (
          <form
            action="https://formspree.io/f/xqegboze"
            method="POST"
            onSubmit={handleSubmit}
            noValidate
          >
            {/* Formspree hidden config fields */}
            <input type="hidden" name="_subject" value="New Contact Form Submission — NovaMind AI" />
            <input type="hidden" name="_next"    value={`${process.env.NEXT_PUBLIC_SITE_URL}/contact?success=true`} />
            {/* Honeypot anti-spam */}
            <input type="text"   name="_gotcha"  style={{ display: 'none' }} />

            <h2 className={styles.formTitle}>Send Us a Message</h2>

            {status === 'error' && (
              <div className={styles.errorBanner}>
                Something went wrong. Please try again or email us directly at {process.env.NEXT_PUBLIC_CONTACT_EMAIL}
              </div>
            )}

            {/* Name + Email row */}
            <div className={styles.row}>
              <div className={styles.fieldGroup}>
                <label htmlFor="name" className={styles.label}>Full Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={name}
                  onChange={e => { setName(e.target.value); clearError('name'); }}
                  className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                  placeholder="Jane Smith"
                />
                {errors.name && <p className={styles.fieldError}>{errors.name}</p>}
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="email" className={styles.label}>Email Address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); clearError('email'); }}
                  className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                  placeholder="jane@company.com"
                />
                {errors.email && <p className={styles.fieldError}>{errors.email}</p>}
              </div>
            </div>

            {/* Subject */}
            <div className={styles.fieldGroup}>
              <label htmlFor="subject" className={styles.label}>Subject</label>
              <input
                id="subject"
                name="subject"
                type="text"
                value={subject}
                onChange={e => { setSubject(e.target.value); clearError('subject'); }}
                className={`${styles.input} ${errors.subject ? styles.inputError : ''}`}
                placeholder="e.g. Demo request, pricing question…"
              />
              {errors.subject && <p className={styles.fieldError}>{errors.subject}</p>}
            </div>

            {/* Message */}
            <div className={styles.fieldGroup}>
              <label htmlFor="message" className={styles.label}>Message</label>
              <textarea
                id="message"
                name="message"
                value={message}
                onChange={e => { setMessage(e.target.value); clearError('message'); }}
                className={`${styles.textarea} ${errors.message ? styles.inputError : ''}`}
                placeholder="Tell us about your business and how we can help…"
                rows={5}
              />
              {errors.message && <p className={styles.fieldError}>{errors.message}</p>}
            </div>

            {/* reCAPTCHA v2 checkbox — auto-resets on expiry */}
            <div className={styles.recaptchaWrap}>
              <div id="recaptcha-container" />
              {errors.recaptcha && <p className={styles.fieldError}>{errors.recaptcha}</p>}
            </div>

            {/* Submit */}
            <button type="submit" className={styles.btnSubmit}>
              Send Message
            </button>

          </form>
        )}
      </div>
    </div>
  );
}

export default function ContactPage() {
  return (
    <div className={styles.page}>

      {/* Top Bar */}
      <div className={styles.topBar}>
        <Link href="/" className={styles.backLink}>← Back to Home</Link>
        <Link href="/" className={styles.logoLink}>NovaMind AI</Link>
      </div>

      <Suspense fallback={<div className={styles.layout} />}>
        <ContactForm />
      </Suspense>

      {/* Page Footer */}
      <div className={styles.pageFooter}>
        <p>
          © {new Date().getFullYear()} NovaMind AI Inc. All rights reserved.{' '}
          <Link href="/privacy" className={styles.footerLink}>Privacy Policy</Link>
          {' · '}
          <Link href="/terms" className={styles.footerLink}>Terms of Service</Link>
          {' · '}
          <Link href="/" className={styles.footerLink}>Back to Home</Link>
        </p>
      </div>

    </div>
  );
}
